import { useQuery } from '@tanstack/react-query';
import { tcgDataService } from '../services/tcgDataService';
import { loadMasterSets } from '../utils/pokemonData';
import { loadOnePieceSets, transformApiCardToOnePieceCard } from '../utils/onepieceData';

const STALE_TIME = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Hook to fetch and cache Pokemon master sets
 */
export function usePokemonSets() {
  return useQuery({
    queryKey: ['pokemonMasterSets'],
    queryFn: async () => {
      console.log('[usePokemonSets] Fetching Pokemon master sets...');
      const sets = await tcgDataService.getPokemonSets();
      return loadMasterSets(sets);
    },
    // staleTime: STALE_TIME,
    // cacheTime: STALE_TIME,
  });
}

/**
 * Hook to fetch and cache One Piece master sets
 */
export function useOnePieceSets() {
  return useQuery({
    queryKey: ['onePieceMasterSets'],
    queryFn: async () => {
      console.log('[useOnePieceSets] Fetching One Piece master sets...');
      const sets = await tcgDataService.getOnePieceSets();
      return loadOnePieceSets(sets);
    },
    // staleTime: STALE_TIME,
    // cacheTime: STALE_TIME,
  });
}

/**
 * Hook to fetch cards for a specific One Piece set
 */
export function useOnePieceSetCards(setId: string | undefined) {
  return useQuery({
    queryKey: ['onePieceSetCards', setId],
    queryFn: async () => {
      if (!setId) return [];
      console.log(`[useOnePieceSetCards] Fetching cards for set ${setId}...`);
      const cards = await tcgDataService.getOnePieceSetCards(setId);
      const mappedCards = cards.map(c => transformApiCardToOnePieceCard(c, setId));
      
      // Deduplicate cards by their ID (which uses card_image_id, preserving parallels/variants)
      const uniqueCardsMap = new Map<string, any>();
      mappedCards.forEach(card => {
        // Prefer cards with images over those without if there's a duplicate ID
        if (!uniqueCardsMap.has(card.id) || (!uniqueCardsMap.get(card.id).images.large && card.images.large)) {
          uniqueCardsMap.set(card.id, card);
        }
      });
      
      return Array.from(uniqueCardsMap.values());
    },
    enabled: !!setId,
    staleTime: STALE_TIME,
  });
}

/**
 * Hook to fetch cards for a specific Pokemon set
 */
export function usePokemonSetCards(setId: string | undefined) {
  return useQuery({
    queryKey: ['pokemonSetCards', setId],
    queryFn: async () => {
      if (!setId) return [];
      console.log(`[usePokemonSetCards] Fetching cards for set ${setId}...`);
      const rawCards = await tcgDataService.getPokemonSetCards(setId);
      return rawCards.map((card: any) => ({
        ...card,
        images: {
          small: card.thumbnailUrl || card.images?.small || '',
          large: card.imageUrl || card.images?.large || '',
        },
      }));
    },
    enabled: !!setId,
    staleTime: STALE_TIME,
  });
}

/**
 * Hook to prefetch market prices for a set's chase cards
 */
export function usePrefetchPricing(setId: string | undefined, game: 'pokemon' | 'onepiece', enabled: boolean = true) {
  return useQuery({
    queryKey: ['prefetchPricing', setId, game],
    queryFn: async () => {
      if (!setId) return null;
      return tcgDataService.prefetchPricing(setId, game);
    },
    enabled: enabled && !!setId,
    staleTime: Infinity,
    cacheTime: 1000 * 60 * 60, // 1 hour
  });
}
